import { useMemo } from "react";
import type { EnvironmentId } from "@cmmd-forge/contracts";

import type { CmmdDeviceRegistryResourceSnapshot } from "../../cmmd/cmmdDeviceRegistryResource";
import type { CmmdForgeRuntimeListSnapshot } from "../../cmmd/cmmdForgeRuntimeListResource";
import type { SavedEnvironmentRuntimeState } from "../../environments/runtime/savedEnvironmentRuntimeStore";
import { useForgeCloudEnvironmentNamingContext } from "../../environments/useForgeCloudEnvironmentNamingContext";
import { makeCloudRuntimeViews } from "./DiagnosticsCloudRuntimes.logic";
import {
  makeDesktopDiagnosticsTargets,
  type DesktopDiagnosticsTarget,
} from "./DiagnosticsDesktopTabs.logic";

/**
 * The targets Diagnostics can inspect: one per cloud Environment, named as
 * Connections names it, plus trusted desktop servers and the last cloud errors.
 */
export function useDiagnosticsTopology(input: {
  readonly cloudCatalog: CmmdForgeRuntimeListSnapshot;
  readonly deviceRegistry: CmmdDeviceRegistryResourceSnapshot;
  readonly runtimeStates: Readonly<Record<EnvironmentId, SavedEnvironmentRuntimeState>>;
}) {
  const namingContext = useForgeCloudEnvironmentNamingContext();
  const cloudRuntimes = useMemo(
    () =>
      makeCloudRuntimeViews({
        catalogRows: input.cloudCatalog.rows,
        environments: input.cloudCatalog.environments,
        namingContext,
      }),
    [input.cloudCatalog.rows, input.cloudCatalog.environments, namingContext],
  );
  const desktopTargets = useMemo<ReadonlyArray<DesktopDiagnosticsTarget>>(
    () =>
      makeDesktopDiagnosticsTargets({
        runtimes: input.deviceRegistry.registry?.runtimes ?? [],
        devices: input.deviceRegistry.registry?.devices ?? [],
      }),
    [input.deviceRegistry.registry],
  );
  const cloudRuntimeErrors = useMemo(
    () =>
      cloudRuntimes.flatMap((runtime) => {
        const state = input.runtimeStates[runtime.environmentId];
        return state?.lastError
          ? [{ label: runtime.label, error: state.lastError, at: state.lastErrorAt }]
          : [];
      }),
    [cloudRuntimes, input.runtimeStates],
  );
  return {
    cloudRuntimes,
    desktopTargets,
    cloudRuntimeErrors,
  } as const;
}
